/**
 * 系统健康透视 — the pure fold from four wire responses to one page model.
 *
 * Everything here is a function of its input. No fetching, no clock, no
 * randomness: the same `PerspectiveInput` always yields a byte-identical
 * `PerspectiveModel`, because the view renders rows in the order this file
 * emits them and an unstable order makes the whole table reshuffle on every
 * 60s poll.
 *
 * ── The joins, and why each key is the one it is ──
 *
 * The four sources were built by three different teams for three different
 * pages, and none of them shares a key convention with the others:
 *
 *   traffic SKU rows      `skuId` + `skuName`     (gateway usage_logs snapshot)
 *   economics rows        `skuId` + `skuName`     (platform revenue_ledger)
 *   routing targets       `sku_id` + `sku_name`   (gateway routing pool)
 *
 * All three carry the marketplace SKU id, so that is the join key; the names
 * are only ever used for display and for the namespace split.
 *
 * ── The supply side has three grains, and they are not interchangeable ──
 *
 * `TrafficProvenance` in `types.ts` is the authority. In short:
 *
 *   upstream          11 in production, measured (`supply.upstreams`)
 *   upstream channel   21, measured (`supply.channels`) — `SupplyChannelNode`
 *   upstream model     73, NOT measured               — `UpstreamModelNode`
 *
 * A routing target terminates at the finest of those: `upstream_channel_slug`
 * + `upstream_model_name`, with a pinned `capability_id`. That pair is what an
 * operator means by "who is carrying this SKU", so it is where every
 * `SupplyLink` ends. An earlier build of this page pointed links at the bare
 * `upstream_id` and dropped the model name; the resulting ladder could not
 * answer which model on which channel was serving a SKU, which was the one
 * question it existed to answer.
 *
 * Because the model grain is unmeasured, an `UpstreamModelNode` borrows its
 * channel's traffic and says so in `trafficProvenance`. Nothing here
 * synthesises a per-model rate — see the `TrafficProvenance` doc comment.
 *
 * ── The unattributed buckets ──
 *
 * `skuId === null` (economics' legacy pre-attribution usage) and
 * `upstream_id === null` (the ledger's unattributed rows) are REAL rows
 * carrying real money. They are kept as their own nodes because the fleet
 * totals only reconcile with the sum of the nodes when they are present. They
 * have no routing targets, so they get no links.
 */

import type {
  BusinessSkuEconomicsResponse,
  GetRoutingConfigSkuSummaryResponse,
  OpsTrafficOverviewResponse,
  RevenueByUpstreamResponse,
  RoutingConfigSkuSummaryTarget,
  UpstreamConfig,
  RoutingSkuPolicyRecord,
  RoutingSkuTargetRecord,
} from '@nadoutong/contracts';
import { firstAttemptShares, shareByTargetId } from '../tenant-routing/firstAttemptShare.ts';
import {
  UNATTRIBUTED,
  channelKey,
  collectSkuChannelLegs,
  legKey,
  type LegIndex,
  collectChannels,
  collectSkus,
  collectUpstreamModels,
  upstreamModelKey,
} from './model-collect.ts';
import {
  EMPTY_TRAFFIC,
  UNSETTLED_MONEY,
  aggregateTraffic,
  gradeSkuTraffic,
  gradeSupplyTraffic,
  moneyFromTotals,
  trafficFromSummary,
  worstGrade,
} from './model-nodes.ts';
import type {
  EdgeRole,
  FleetSummary,
  HealthGrade,
  MeasuredLeg,
  NamespaceNode,
  ObservedTraffic,
  PerspectiveModel,
  PerspectiveWindow,
  PerspectiveWindowBounds,
  SkuNode,
  SliceFailure,
  SupplyChannelNode,
  SupplyLink,
  TriagedRisk,
  UpstreamModelNode,
} from './types.ts';

export function skuNodeId(skuId: string | null): string {
  return `sku:${skuId ?? UNATTRIBUTED}`;
}

/** `key` comes from `channelKey`, never from a raw slug. */
export function channelNodeId(key: string): string {
  return `channel:${key}`;
}

/** `key` comes from `upstreamModelKey` — `${channel}/${model}`. */
export function upstreamModelNodeId(key: string): string {
  return `upstream_model:${key}`;
}

export function namespaceNodeId(namespace: string): string {
  return `namespace:${namespace}`;
}

export interface PerspectiveInput {
  readonly window: PerspectiveWindow;
  readonly traffic: OpsTrafficOverviewResponse | null;
  readonly skuEconomics: BusinessSkuEconomicsResponse | null;
  readonly upstreamRevenue: RevenueByUpstreamResponse | null;
  readonly routing: GetRoutingConfigSkuSummaryResponse | null;
  /**
   * `GET /v1/ops/upstreams` — the supplier NAMES.
   *
   * Its own slice because nothing else can supply them:
   * `OpsTrafficBreakdownRow.upstreamName` is contractual but is filled in by
   * neither the gateway projection nor ops-api's `decorateRow`, so reading it
   * yields null on every row in production. Null here means the read failed;
   * every name falls back to a channel slug and the page still renders.
   */
  readonly upstreams: readonly UpstreamConfig[] | null;
  readonly failures: readonly SliceFailure[];
}

/**
 * Split a canonical `namespace/model` SKU name on the FIRST slash.
 *
 * A model segment may itself contain slashes (vendor-qualified names do), so
 * only the first separator is structural. A name that does not parse — no
 * slash, a leading slash, or a trailing slash — yields nulls, and the SKU then
 * sits in the table with no namespace rather than under a fabricated one.
 */
function parseSkuName(name: string | null): {
  readonly namespace: string | null;
  readonly model: string | null;
} {
  if (name === null || name.length === 0) return { namespace: null, model: null };
  const slash = name.indexOf('/');
  if (slash <= 0 || slash === name.length - 1) return { namespace: null, model: null };
  return { namespace: name.slice(0, slash), model: name.slice(slash + 1) };
}

/**
 * Widen a SKU-summary target to the record shape `firstAttemptShares` takes.
 *
 * The share helper reads only `id`, `enabled` and `weight`; every other field
 * here is structural padding so the two wire types line up without a cast.
 * The padding is never read — asserted by the fact that this is the only
 * caller and the helper's body is three fields wide — and it must never be
 * plumbed anywhere else, which is why it stays a local function.
 *
 * The alternative, reimplementing the classification and the largest-
 * remainder rounding, is what produces the 33/33/33 = 99% displays the
 * tenant-routing page already solved.
 */
function toShareRecord(target: RoutingConfigSkuSummaryTarget): RoutingSkuTargetRecord {
  return {
    id: target.id,
    sku_id: target.sku_id,
    sku_name: target.sku_name,
    vendor: target.vendor ?? '',
    model: target.model,
    capability_id: target.capability_id ?? null,
    capability_protocol: target.capability_protocol ?? null,
    upstream_model_name: target.upstream_model_name ?? null,
    capability_status: target.capability_status ?? null,
    capability_last_passed_at: target.capability_last_passed_at ?? null,
    upstream_id: target.upstream_id ?? '',
    account_id: target.account_id ?? '',
    key_bucket_id: target.key_bucket_id ?? '',
    upstream_channel_id: target.upstream_channel_id ?? '',
    upstream_channel_slug: target.upstream_channel_slug ?? '',
    enabled: target.enabled,
    weight: target.weight,
    retry_order: target.retry_order,
    priority: target.priority,
  };
}

/**
 * The same classification `firstAttemptShare.ts` applies, used ONLY when the
 * share lookup misses — which requires two rows in one pool to share an id,
 * something the gateway's primary key prevents. It exists so an impossible
 * duplicate drops a share number rather than dropping the ladder rung itself:
 * a rung missing from the table is invisible, a mis-shaded one is not.
 */
function fallbackRole(target: RoutingConfigSkuSummaryTarget): EdgeRole {
  if (!target.enabled) return 'disabled';
  return target.weight > 0 ? 'active' : 'reserve';
}

/** Per-SKU target-pool arithmetic: the ladder rungs and the node's three counts. */
interface PoolResult {
  readonly links: readonly SupplyLink[];
  readonly activeTargetCount: number;
  readonly reserveTargetCount: number;
  readonly disabledTargetCount: number;
}

const EMPTY_POOL: PoolResult = {
  links: [],
  activeTargetCount: 0,
  reserveTargetCount: 0,
  disabledTargetCount: 0,
};

/**
 * What this rung actually carried FOR THIS SKU, or null when the pair was not
 * observed — the SKU sent this rung nothing, and the renderer must say that
 * rather than borrowing the channel's figure, which is a different quantity
 * and can invert the verdict (see `MeasuredLeg`).
 *
 * The sound key first, then the narrow compatibility fallback, then the slug.
 * Never a guess: both fallbacks are already empty for a key two legs of this
 * SKU share, so an ambiguous rung reads unknown instead of another supplier's
 * numbers.
 */
function measuredLeg(
  legs: LegIndex,
  skuId: string,
  channelId: string | null,
  slug: string | null,
  channel: string,
): MeasuredLeg | null {
  if (channelId !== null) {
    return (
      legs.byChannel.get(legKey(skuId, channel)) ??
      legs.byChannelId.get(legKey(skuId, channelId)) ??
      null
    );
  }
  return legs.bySlug.get(legKey(skuId, slug)) ?? null;
}

/**
 * Fold one SKU's target pool into ladder rungs + counts.
 *
 * The counts are derived from the SAME `firstAttemptShares` call that produced
 * the rung roles, so a node claiming "2 active" and a table drawing three
 * active rungs is structurally impossible.
 */
function buildPool(
  skuId: string,
  targets: readonly RoutingConfigSkuSummaryTarget[],
  legs: LegIndex,
  names: ReadonlyMap<string, string>,
): PoolResult {
  const shares = shareByTargetId(firstAttemptShares(targets.map(toShareRecord)));
  const links: SupplyLink[] = [];
  let active = 0;
  let reserve = 0;
  let disabled = 0;

  for (const target of targets) {
    const share = shares.get(target.id);
    const role: EdgeRole = share?.kind ?? fallbackRole(target);
    if (role === 'active') active += 1;
    else if (role === 'reserve') reserve += 1;
    else disabled += 1;

    // No supply identity at all means no endpoint to point at. The target
    // still counted above, because it is really in the pool.
    if (target.upstream_id === undefined) continue;
    const slug = target.upstream_channel_slug ?? null;
    const channelId = target.upstream_channel_id ?? null;
    const channel = channelKey(channelId, slug, target.upstream_id);
    links.push({
      id: target.id,
      fromId: skuNodeId(skuId),
      toId: upstreamModelNodeId(upstreamModelKey(channel, target.upstream_model_name ?? null)),
      // Denormalised so a ladder row renders without a second lookup.
      channelSlug: slug,
      channelId,
      upstreamId: target.upstream_id,
      // By id. A slug-keyed join would put `ucloud`'s traffic under
      // `jojokey` — both own a channel called `seedance`.
      upstreamName: names.get(target.upstream_id) ?? null,
      role,
      // 0 for reserve/disabled by definition — see `EdgeRole` in types.ts.
      configuredSharePct: share?.percent ?? 0,
      weight: target.weight,
      priority: target.priority,
      retryOrder: target.retry_order,
      capabilityStatus: target.capability_status ?? null,
      upstreamModelName: target.upstream_model_name ?? null,
      // By (upstream, channel id) — see `measuredLeg`. Keying this by slug
      // merged two rungs onto ONE leg whenever a SKU routed to same-named
      // channels of different suppliers: both rendered the same volume, and
      // the SKU's rungs summed to twice the traffic it served.
      measured: measuredLeg(legs, skuId, channelId, slug, channel),
    });
  }

  return {
    links,
    activeTargetCount: active,
    reserveTargetCount: reserve,
    disabledTargetCount: disabled,
  };
}

function groupTargetsBySku(
  targets: readonly RoutingConfigSkuSummaryTarget[],
): ReadonlyMap<string, readonly RoutingConfigSkuSummaryTarget[]> {
  const grouped = new Map<string, RoutingConfigSkuSummaryTarget[]>();
  for (const target of targets) {
    const existing = grouped.get(target.sku_id);
    if (existing === undefined) grouped.set(target.sku_id, [target]);
    else existing.push(target);
  }
  return grouped;
}

function policyBySku(
  policies: readonly RoutingSkuPolicyRecord[],
): ReadonlyMap<string, RoutingSkuPolicyRecord['policy_mode']> {
  return new Map(policies.map((policy) => [policy.sku_id, policy.policy_mode]));
}

/**
 * Requests desc, then label asc. Ties on request count are common (every idle
 * node is tied at 0, and every model on one channel is tied at its channel's
 * count), so the name tiebreak is what actually keeps the order stable across
 * refetches.
 */
function byRequestsThenLabel(
  a: { readonly traffic: ObservedTraffic; readonly label: string; readonly id: string },
  b: { readonly traffic: ObservedTraffic; readonly label: string; readonly id: string },
): number {
  if (b.traffic.requests !== a.traffic.requests) return b.traffic.requests - a.traffic.requests;
  if (a.label !== b.label) return a.label < b.label ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * `upstream_id` → supplier name.
 *
 * Keyed by id and never by channel slug: the slug `seedance` belongs to two
 * different upstreams (`jojokey` and `ucloud`) in production, so a
 * slug-keyed map attributes one supplier's traffic to the other. Routing
 * targets carry `upstream_id`, so the sound key is always available.
 */
function upstreamNames(upstreams: readonly UpstreamConfig[] | null): ReadonlyMap<string, string> {
  return new Map((upstreams ?? []).map((upstream) => [upstream.id, upstream.name] as const));
}

function buildSkuNodes(
  input: PerspectiveInput,
  trafficLoaded: boolean,
): { readonly skus: readonly SkuNode[]; readonly links: readonly SupplyLink[] } {
  const accumulators = collectSkus(
    input.traffic?.products.skus ?? [],
    // A null `skus` section means the economics slice degraded; the caller has
    // already recorded that as a `SliceFailure`, and the SKUs still appear from
    // the other two sources with unsettled money.
    input.skuEconomics?.skus ?? [],
    input.routing?.targets ?? [],
  );
  const targetsBySku = groupTargetsBySku(input.routing?.targets ?? []);
  const policies = policyBySku(input.routing?.policies ?? []);
  // Absent on a gateway that cannot report the group: every rung then carries
  // a null leg, and `skuChannelsReported` is what tells the renderer that the
  // nulls mean "unknown" rather than "carried nothing".
  const legs = collectSkuChannelLegs(input.traffic?.supply.skuChannels ?? []);
  const names = upstreamNames(input.upstreams);
  const links: SupplyLink[] = [];

  const skus = [...accumulators.values()].map((accumulator): SkuNode => {
    const { namespace, model } = parseSkuName(accumulator.name);
    const pool =
      accumulator.skuId === null
        ? EMPTY_POOL
        : buildPool(accumulator.skuId, targetsBySku.get(accumulator.skuId) ?? [], legs, names);
    links.push(...pool.links);
    return {
      id: skuNodeId(accumulator.skuId),
      kind: 'sku',
      // The null bucket has no name and no id, so its label is empty and the
      // UI supplies a localized one off `skuId === null`. Every other SKU
      // falls back to its id rather than rendering blank.
      label: accumulator.name ?? accumulator.skuId ?? '',
      // Downstream: `downstreamErrorRate`. A rescued request was a success for
      // the customer, and a request our own gate refused was never ours to
      // fail — both leave the verdict. See `gradeSkuTraffic`.
      grade: gradeSkuTraffic(accumulator.traffic, trafficLoaded, accumulator.refusalsReported),
      traffic: accumulator.traffic,
      skuId: accumulator.skuId,
      namespace,
      model,
      money: accumulator.money,
      coverage: accumulator.coverage,
      coverageStatus: accumulator.coverageStatus,
      activeTargetCount: pool.activeTargetCount,
      reserveTargetCount: pool.reserveTargetCount,
      disabledTargetCount: pool.disabledTargetCount,
      policyMode: accumulator.skuId === null ? null : policies.get(accumulator.skuId) ?? null,
    };
  });

  return { skus: [...skus].sort(byRequestsThenLabel), links };
}

/**
 * Distinct SKUs reaching each supply node through a NON-disabled rung.
 *
 * Disabled rows are excluded because "serves" is a statement about live supply,
 * and a switched-off target serves nothing.
 */
function servedSkus(
  links: readonly SupplyLink[],
  keyOf: (link: SupplyLink) => string,
): ReadonlyMap<string, ReadonlySet<string>> {
  const served = new Map<string, Set<string>>();
  for (const link of links) {
    if (link.role === 'disabled') continue;
    const key = keyOf(link);
    const existing = served.get(key);
    if (existing === undefined) served.set(key, new Set([link.fromId]));
    else existing.add(link.fromId);
  }
  return served;
}

interface SupplyNodes {
  readonly channels: readonly SupplyChannelNode[];
  readonly upstreamModels: readonly UpstreamModelNode[];
}

/**
 * The two supply layers, built together because the finer one borrows from the
 * coarser one and the coarser one counts the finer one.
 */
function buildSupplyNodes(
  input: PerspectiveInput,
  trafficLoaded: boolean,
  links: readonly SupplyLink[],
): SupplyNodes {
  const names = upstreamNames(input.upstreams);
  const channelAccumulators = collectChannels(
    // `supply.channels`, NOT `supply.upstreams`: the channel is the finest
    // grain that reports its own error rate, and an upstream-grain rate cannot
    // say which of its channels is the broken one.
    input.traffic?.supply.channels ?? [],
    input.upstreamRevenue?.upstreams ?? [],
    input.routing?.targets ?? [],
  );
  const modelAccumulators = collectUpstreamModels(input.routing?.targets ?? []);

  const skusByChannel = servedSkus(links, (link) =>
    channelKey(link.channelId, link.channelSlug, link.upstreamId),
  );
  const skusByModel = servedSkus(links, (link) => link.toId);

  const modelsPerChannel = new Map<string, number>();
  for (const accumulator of modelAccumulators.values()) {
    modelsPerChannel.set(accumulator.channelKey, (modelsPerChannel.get(accumulator.channelKey) ?? 0) + 1);
  }

  const channels = [...channelAccumulators.values()].map((accumulator): SupplyChannelNode => ({
    id: channelNodeId(accumulator.key),
    kind: 'channel',
    // The slug IS this grain's identity, so it leads. The upstream name is
    // contractual but unpopulated today (see `ChannelAccumulator`), and the raw
    // upstream id is the last resort rather than a blank cell.
    label: accumulator.channelSlug ?? accumulator.upstreamName ?? accumulator.upstreamId ?? '',
    // Supply: 5xx only. The same channel can be `ok` here while the SKUs
    // riding it are `bad`, and that contrast is the finding — see
    // `gradeSupplyTraffic`.
    grade: gradeSupplyTraffic(accumulator.traffic, trafficLoaded),
    traffic: accumulator.traffic,
    channelSlug: accumulator.channelSlug,
    upstreamId: accumulator.upstreamId,
    // The upstreams read wins over the traffic row's own field, which is
    // contractual and never populated. Falls through to null, and the label
    // above still has the slug.
    upstreamName:
      (accumulator.upstreamId === null ? null : names.get(accumulator.upstreamId) ?? null) ??
      accumulator.upstreamName,
    // The OWNING UPSTREAM's figure, borrowed down one grain. `NodeMoney` has no
    // provenance flag to carry that, so it is stated here and in
    // `ChannelAccumulator.owningUpstreamMoney`: every sibling channel of one
    // upstream reports the same number, and summing across channels
    // double-counts. The fleet headline does not come from here — it comes from
    // `sku-economics` totals — so nothing reconciles against this sum.
    money: accumulator.owningUpstreamMoney,
    servesSkuCount: skusByChannel.get(accumulator.key)?.size ?? 0,
    modelCount: modelsPerChannel.get(accumulator.key) ?? 0,
  }));

  const upstreamModels = [...modelAccumulators.values()].map((accumulator): UpstreamModelNode => {
    const channel = channelAccumulators.get(accumulator.channelKey);
    // Borrowed, never synthesised. A model whose channel is somehow absent
    // reports nothing rather than a number it made up.
    const traffic = channel?.traffic ?? EMPTY_TRAFFIC;
    return {
      id: upstreamModelNodeId(accumulator.key),
      kind: 'upstream_model',
      label: accumulator.upstreamModelName ?? accumulator.channelSlug ?? accumulator.upstreamId ?? '',
      // Supply, on borrowed numbers: the grade is its channel's verdict, for
      // the same reason `traffic` is its channel's figures.
      grade: gradeSupplyTraffic(traffic, trafficLoaded),
      traffic,
      channelSlug: accumulator.channelSlug,
      upstreamId: accumulator.upstreamId,
      upstreamName:
        (accumulator.upstreamId === null ? null : names.get(accumulator.upstreamId) ?? null) ??
        channel?.upstreamName ??
        null,
      upstreamModelName: accumulator.upstreamModelName,
      channelNodeId: channel === undefined ? null : channelNodeId(channel.key),
      // Always this literal — the grain has no measurement of its own, so there
      // is no branch where it could honestly be `measured`.
      trafficProvenance: 'channel_borrowed',
      capabilityStatus: accumulator.capabilityStatus,
      capabilityId: accumulator.capabilityId,
      servesSkuCount: skusByModel.get(upstreamModelNodeId(accumulator.key))?.size ?? 0,
    };
  });

  return {
    channels: [...channels].sort(byRequestsThenLabel),
    upstreamModels: [...upstreamModels].sort(byRequestsThenLabel),
  };
}

/**
 * Namespace trunk nodes, derived purely from the SKUs that parsed one.
 *
 * The grade is the worst among the namespace's SKUs rather than a re-grade of
 * the aggregated rates: one SKU failing every request inside an otherwise
 * healthy namespace must colour the trunk, and summing first would dilute it
 * below every threshold.
 *
 * There are no namespace→SKU links. `SupplyLink` is a ladder rung — its
 * `fromId` is a SKU and its `toId` an upstream model, both by contract — and
 * the namespace membership it used to encode is already on
 * `NamespaceNode.skuIds`.
 */
function buildNamespaceNodes(skus: readonly SkuNode[]): readonly NamespaceNode[] {
  const grouped = new Map<string, SkuNode[]>();
  for (const sku of skus) {
    if (sku.namespace === null) continue;
    const existing = grouped.get(sku.namespace);
    if (existing === undefined) grouped.set(sku.namespace, [sku]);
    else existing.push(sku);
  }

  const namespaces = [...grouped.entries()].map(([namespace, members]): NamespaceNode => ({
    id: namespaceNodeId(namespace),
    kind: 'namespace',
    label: namespace,
    grade: worstGrade(members.map((member) => member.grade)),
    traffic: aggregateTraffic(members.map((member) => member.traffic)),
    namespace,
    // `skus` is already sorted, so this list is too.
    skuIds: members.map((member) => member.id),
  }));

  return [...namespaces].sort((a, b) =>
    a.namespace < b.namespace ? -1 : a.namespace > b.namespace ? 1 : 0,
  );
}

/** Traffic-overview risks, narrowed. Passed through, never re-triaged. */
function narrowRisks(input: PerspectiveInput): readonly TriagedRisk[] {
  return (input.traffic?.risks ?? []).map((risk) => ({
    id: risk.id,
    severity: risk.severity,
    kind: risk.kind,
    label: risk.label,
    currentRate: risk.currentRate,
    thresholdRate: risk.thresholdRate,
    affectedCount: risk.affectedCount,
  }));
}

/**
 * The interval both data layers describe.
 *
 * Traffic is authoritative because the money reads are driven from the bounds
 * it echoes back. When traffic failed there are no authoritative bounds, so
 * whichever money slice answered is used instead, and finally empty strings —
 * a synthesized interval derived from the preset would claim a window nothing
 * was actually measured over.
 */
function resolveBounds(input: PerspectiveInput): PerspectiveWindowBounds {
  const source = input.traffic?.window ?? input.skuEconomics?.window ?? input.upstreamRevenue?.window;
  return {
    preset: input.window,
    from: source?.from ?? '',
    to: source?.to ?? '',
  };
}

interface SummaryCounts {
  readonly skuCount: number;
  readonly channelCount: number;
  readonly upstreamModelCount: number;
}

function buildSummary(
  input: PerspectiveInput,
  trafficLoaded: boolean,
  grades: readonly HealthGrade[],
  counts: SummaryCounts,
): FleetSummary {
  const traffic =
    input.traffic === null ? EMPTY_TRAFFIC : trafficFromSummary(input.traffic.summary);
  const totals = input.skuEconomics?.totals ?? null;
  const coverage = input.skuEconomics?.coverage ?? null;
  return {
    traffic,
    money: totals === null ? UNSETTLED_MONEY : moneyFromTotals(totals),
    // Worst of every downstream node, not a re-grade of the fleet aggregate:
    // the gateway already grades the aggregate and publishes it as `risks`,
    // and this page's job is to surface the worst thing on the page. One SKU
    // failing everything inside a large healthy fleet must show, and summing
    // first would dilute it below every threshold.
    grade: trafficLoaded ? worstGrade(grades) : 'unknown',
    skuCount: counts.skuCount,
    channelCount: counts.channelCount,
    upstreamModelCount: counts.upstreamModelCount,
    // Null, not 0, when the coverage slice degraded — "no unrouted SKUs" and
    // "we could not check" are different answers.
    unroutedSkuCount: coverage?.unroutedSkuCount ?? null,
    singlePointSkuCount: coverage?.singlePointSkuCount ?? null,
    unhealthyUpstreamCount: coverage?.unhealthyUpstreamCount ?? null,
    // The population those two counters were computed over. See
    // `FleetSummary.pricedSkuCount` — it is NOT `counts.skuCount`.
    pricedSkuCount: coverage?.pricedSkuCount ?? null,
  };
}

/**
 * Ladder order: source SKU, then retry-ladder position, then weight desc.
 *
 * `priority` first because that is the ladder the gateway walks; weight desc
 * inside a priority so the heaviest first-attempt target reads top-most. The
 * id tiebreak is what makes the sort total — without it two identical rows
 * could swap places between polls and the table would jitter.
 */
function sortLinks(links: readonly SupplyLink[]): readonly SupplyLink[] {
  return [...links].sort((a, b) => {
    if (a.fromId !== b.fromId) return a.fromId < b.fromId ? -1 : 1;
    if (a.priority !== b.priority) return a.priority - b.priority;
    if (a.weight !== b.weight) return b.weight - a.weight;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function buildPerspectiveModel(input: PerspectiveInput): PerspectiveModel {
  // The one condition that turns every grade to `unknown`. Derived from the
  // payload, not from the failure list, so a model built by hand in a test
  // grades the same way the page does.
  const trafficLoaded = input.traffic !== null;

  const { skus, links } = buildSkuNodes(input, trafficLoaded);
  const { channels, upstreamModels } = buildSupplyNodes(input, trafficLoaded, links);
  const namespaces = buildNamespaceNodes(skus);

  // DOWNSTREAM subjects only. The fleet headline is a customer-experience
  // statement, so a channel graded on 5xx must not colour it: the two verdicts
  // answer different questions and blending them produces a headline that is
  // neither. The supply figure travels alongside on `summary.traffic`
  // (`serverErrors` / `serverErrorRate`), unblended, for a renderer to state
  // as its own sentence.
  //
  // A SKU whose gateway cannot report refusals contributes `unknown` here, so
  // the headline degrades to "we could not decide" rather than inheriting a
  // verdict computed from a substituted zero.
  //
  // Upstream-model grades are absent for a second reason as well: each is its
  // channel's grade borrowed, so it is never independent evidence.
  const grades = [...namespaces.map((node) => node.grade), ...skus.map((node) => node.grade)];

  return {
    window: resolveBounds(input),
    generatedAt: input.traffic?.window.refreshedAt ?? input.skuEconomics?.generatedAt ?? '',
    summary: buildSummary(input, trafficLoaded, grades, {
      skuCount: skus.length,
      channelCount: channels.length,
      upstreamModelCount: upstreamModels.length,
    }),
    namespaces,
    skus,
    channels,
    upstreamModels,
    links: sortLinks(links),
    risks: narrowRisks(input),
    failures: input.failures,
    routingConfigName: input.routing?.config.name ?? null,
    // `undefined` is the gateway saying it cannot report per-SKU legs. An
    // empty array is a gateway that CAN and observed none, which is a real
    // (if unlikely) window and reads as "carried nothing", not as unknown.
    skuChannelsReported: input.traffic?.supply.skuChannels !== undefined,
  };
}
